/**
 * Creates a cropped image from canvas and returns a File object
 * @param {HTMLImageElement} image - Image element (for react-image-crop)
 * @param {Object} crop - Crop data from react-image-crop { x, y, width, height, unit }
 * @param {number} cropSize - Size of the square crop area
 * @param {string} fileName - Original filename for the cropped file
 * @returns {Promise<File>} - Cropped image as File object
 */
export const getCroppedImg = (image, crop, cropSize = 320, fileName = 'cropped-image.jpg') => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')

    if (!image || !crop) {
      reject(new Error('Image or crop data missing'))
      return
    }

    // Calculate actual pixel values from percentage if needed
    const pixelCrop = {
      x: crop.unit === '%' ? (crop.x * image.naturalWidth) / 100 : crop.x,
      y: crop.unit === '%' ? (crop.y * image.naturalHeight) / 100 : crop.y,
      width: crop.unit === '%' ? (crop.width * image.naturalWidth) / 100 : crop.width,
      height: crop.unit === '%' ? (crop.height * image.naturalHeight) / 100 : crop.height
    }

    // Set canvas size to desired crop size (square)
    canvas.width = cropSize
    canvas.height = cropSize

    // Draw the cropped portion of the image
    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      cropSize,
      cropSize
    )

    // Convert canvas to blob and then to File
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Canvas is empty'))
          return
        }

        // Create File from blob with original filename
        const file = new File([blob], fileName, {
          type: 'image/jpeg',
          lastModified: Date.now()
        })

        resolve(file)
      },
      'image/jpeg',
      0.9
    )
  })
}

/**
 * Creates area for square crop (1:1 aspect ratio)
 * @param {number} imageWidth - Original image width
 * @param {number} imageHeight - Original image height
 * @returns {Object} - Initial crop area
 */
export const getInitialCropArea = (imageWidth, imageHeight) => {
  const size = Math.min(imageWidth, imageHeight)
  const x = (imageWidth - size) / 2
  const y = (imageHeight - size) / 2

  return {
    x,
    y,
    width: size,
    height: size
  }
}
